import { ctaDesc, ctaTitle } from "@/lib/seo";
// /embassy/$country/$service — programmatic combo landing pages
// 8 services × ~30 countries = ~240 long-tail pages
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { EMBASSY_COUNTRIES, getEmbassy } from "@/data/embassies";
import { EMBASSY_SERVICES, getEmbassyService } from "@/data/embassy-services";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { EmbassyMarriageLinks } from "@/components/EmbassyMarriageLinks";
import { CountryDeepContent } from "@/components/CountryDeepContent";
import { isHagueSlug } from "@/lib/country-hague";
import { BadgeCheck, ShieldCheck, FileCheck2, Phone, MessageCircle, Clock, ArrowRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/embassy/$country/$service")({
  loader: ({ params }) => {
    const c = getEmbassy(params.country);
    const s = getEmbassyService(params.service);
    if (!c || !s) throw notFound();
    return { country: params.country, service: params.service };
  },
  head: ({ params }) => {
    const c = getEmbassy(params.country);
    const s = getEmbassyService(params.service);
    if (!c || !s) return { meta: [{ title: ctaTitle("ไม่พบบริการ", "/embassy/$country/$service", "th")}] };
    const url = `${SITE}/embassy/${c.slug}/${s.slug}`;
    const totalFee = c.serviceFeeTh + s.feeDeltaTh;
    const title = `${s.nameTh}สถานทูต${c.nameTh} ${c.flag} เริ่ม ฿${totalFee.toLocaleString()} | NYC`;
    const desc = `${s.nameTh} สำหรับยื่นสถานทูต${c.nameTh}: ${s.summary} เสร็จ ${s.turnaround ?? c.turnaround} — รับรองโดยนักแปลขึ้นทะเบียน MFA Thailand — ทักไลน์เพื่อรับเช็กลิสต์เอกสารทันที`;
    return {
      meta: [
        { title: ctaTitle(title, "/embassy/$country/$service", "th") },
        { name: "description", content: ctaDesc(desc, "/embassy/$country/$service", "th")},
        { name: "keywords", content: [...c.keywords, s.nameTh, s.nameEn].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/embassy/$country/$service", "th")},
        { property: "og:description", content: ctaDesc(desc, "/embassy/$country/$service", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${s.nameTh}สถานทูต${c.nameTh}`,
              serviceType: s.nameEn,
              areaServed: [
                { "@type": "Country", name: "Thailand" },
                { "@type": "Country", name: c.nameEn },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: totalFee,
                priceCurrency: "THB",
                availability: "https://schema.org/InStock",
              },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: [...s.faqs, ...c.faqs.slice(0, 2)].map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `วิธี${s.nameTh} สำหรับสถานทูต${c.nameTh}`,
              totalTime: "P7D",
              step: s.steps.map((step, i) => ({
                "@type": "HowToStep",
                position: i + 1,
                name: `Step ${i + 1}`,
                text: step,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "รับรองสถานทูต", item: `${SITE}/embassy` },
                { "@type": "ListItem", position: 3, name: c.nameTh, item: `${SITE}/embassy/${c.slug}` },
                { "@type": "ListItem", position: 4, name: s.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: EmbassyServicePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบบริการ</h1>
      <Link to="/embassy" className="mt-4 inline-block text-gold underline">กลับไปยังรายการประเทศทั้งหมด</Link>
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  ),
});

function EmbassyServicePage() {
  const { country, service } = Route.useLoaderData() as { country: string; service: string };
  const c = getEmbassy(country)!;
  const s = getEmbassyService(service)!;
  const totalFee = c.serviceFeeTh + s.feeDeltaTh;
  const turnaround = s.turnaround ?? c.turnaround;
  const otherServices = EMBASSY_SERVICES.filter((x) => x.slug !== service).slice(0, 6);
  const otherCountries = EMBASSY_COUNTRIES.filter((x) => x.slug !== country && x.region === c.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/embassy" className="hover:text-gold">รับรองสถานทูต</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/embassy/$country" params={{ country }} className="hover:text-gold">{c.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{s.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <BadgeCheck className="h-3.5 w-3.5" /> {s.nameEn} · {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{c.flag}</span>
            {s.nameTh}สถานทูต{c.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{s.summary}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคารวม", v: `฿${totalFee.toLocaleString()}` },
              { k: "เสร็จภายใน", v: turnaround },
              { k: "ค่าสถานทูต", v: `฿${c.govFeeTh.toLocaleString()}` },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((x) => (
              <div key={x.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{x.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{x.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ส่งเอกสารผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service ให้บริการ{s.nameTh}สำหรับสถานทูต{c.nameTh} ครบทุกขั้นตอน — แปลโดยนักแปลขึ้นทะเบียน MFA + รับรองนิติกรณ์ + ยื่นสถานทูต/กงสุล{c.nameTh} รวม ฿{totalFee.toLocaleString()} เสร็จ {turnaround}
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ขั้นตอนการทำงาน</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {s.steps.map((step, i) => (
              <li key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {i + 1}</div>
                <div className="mt-1 text-sm font-semibold text-navy-deep">{step}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารยอดนิยมสำหรับ{c.nameTh}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {c.topDocs.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา One-Stop</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {s.nameTh}สถานทูต{c.nameTh} ฿{totalFee.toLocaleString()}/ฉบับ
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมแปล + MFA + สถานทูตในราคาเดียว</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3 วันค่าเร่ง +฿3,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ส่ง EMS ฉบับจริงทั่วประเทศฟรี</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ปรึกษานักแปล/ทนายฟรีก่อนสั่งทำ</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[...s.faqs, ...c.faqs.slice(0, 2)]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">บริการอื่นสำหรับสถานทูต{c.nameTh}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherServices.map((o) => (
              <Link
                key={o.slug}
                to="/embassy/$country/$service"
                params={{ country, service: o.slug }}
                className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="flex-1">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{o.nameEn}</div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        {otherCountries.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{s.nameTh}สำหรับประเทศใกล้เคียง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherCountries.map((o) => (
                <Link
                  key={o.slug}
                  to="/embassy/$country/$service"
                  params={{ country: o.slug, service }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{(o.serviceFeeTh + s.feeDeltaTh).toLocaleString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
        <EmbassyMarriageLinks country={c.slug} countryTh={c.nameTh} />
      </div>

      <CountryDeepContent
        lang="th"
        service="embassy"
        countryTh={c.nameTh}
        countryEn={c.nameEn}
        flag={c.flag}
        hague={isHagueSlug(c.slug)}
        turnaround={turnaround}
        priceFromTh={totalFee}
        slug={c.slug}
      />


      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
    </main>
  );
}
